import { useEffect, useState } from 'react';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form, FormParagraph, HeaderDivider } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { CheckboxField } from '../../../../fields/Checkbox';
import { TextField } from '../../../../fields/Text';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';

export interface ICustomControlDataGto {
    emailAddress?: string;
    toestemming: boolean;
}

export function GtoV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const { patient } = useEpisodeContext();
    const [buttonDisabled, setButtonDisabled] = useState(false);

    useEffect(() => {
        if (patient.emailAddress && patient.emailAddress !== props.request.customControlData.emailAddress) {
            props.handleChangeCustomControl({ emailAddress: patient.emailAddress });
        }
    }, []);

    return (
        <NewOrEditRequestProcessContainer
            isExaminationRequest
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            process={props.process}
            service={props.service}
            title={getServiceLabel(props.service.code, 'request', props.service.name)}
        >
            {addEditForm()}
        </NewOrEditRequestProcessContainer>
    );

    function addEditForm() {
        return (
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...props}
                    entity={props.request}
                />

                <FormParagraph>
                    Je kunt alleen een thuisconsult starten als de patiënt in staat is om zelf foto's te uploaden via
                    mobiel, tablet of computer.
                </FormParagraph>

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'request',
                        item: props.request.customControlData,
                        errors: props.errors,
                        customControlData: true,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <TextField
                        fieldKey="emailAddress"
                        isRequired
                        label={translateKey('request.patientEmail')}
                        placeholder={translateKey('common.emailAddress')}
                    />

                    <FormSection type="referral">
                        <HeaderDivider text="Toestemming voor mogelijk consult" />
                        <InputControl isFormControl>
                            <CheckboxField
                                fieldKey="toestemming"
                                label=""
                                text="De patiënt geeft toestemming voor een consult indien de huisarts dit nodig acht."
                            />
                        </InputControl>
                    </FormSection>
                </FormContextProvider>

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />,
                    ]}
                />
            </Form>
        );
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }

        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                undefined,
                props.service,
                'request',
                `Het onderzoek is verstuurd naar ${getDisplayName(patient)}. Volg de status via je takenlijst.`
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        if (props.reloadPatient) {
            await props.reloadPatient();
        }
        await props.onSave(requestId);
    }
}

export function gtoV1RequestInitializeCustomControlData(): ICustomControlDataGto {
    return {
        emailAddress: undefined,
        toestemming: false,
    };
}
